import { addHours, subDays } from "date-fns";
import { Option } from "shared/model/Option";

export interface RequestLog {
    id: number;
    agentName?: string;
    target: string;
    processVersion?: number;
    processExecutionId?: string;
    routeTemplate: string;
    path: string;
    origin: string;
    queryString?: string;
    method: HttpMethod;
    statusCode: number;
    error?: string;
    startTimeUtc: Date;
    durationInMilliseconds: number;
    requestHeaders?: string;
    responseHeaders?: string;
    requestLength?: number;
    responseLength?: number;
    remoteIpAddress?: string;
    identity: string;
    requestBody?: string;
    responseBody?: string;
    requestBodyClipped?: boolean;
    responseBodyClipped?: boolean;
}

export enum ApiMonitoringQueryParam {
    RouteTemplate = 'routeTemplate',
    StatusCodes = 'statusCodes',
    Methods = 'methods',
    StartTimeLimit = 'startTimeLimit',
    EndTimeLimit = 'endTimeLimit',
    Path = 'path',
    MinDurationMilliSeconds = 'minDurationMilliSeconds',
    MaxDurationMilliSeconds = 'maxDurationMilliSeconds',
    QueryString = 'queryString',
    RemoteIpAddress = 'remoteIpAddress',
    AgentName = 'agentName',
    IdentityName = 'identityName',
}

export enum HttpStatusCode {
    '1XX' = 'Informational',
    '2XX' = 'Success',
    '3XX' = 'Redirect',
    '4XX' = 'ClientError',
    '5XX' = 'ServerError',
}

export enum HttpMethod {
    GET = 'Get',
    POST = 'Post',
    PUT = 'Put',
    PATCH = 'Patch',
    DELETE = 'Delete',
    HEAD = 'Head',
    OPTIONS = 'Options'
}

export enum RequestDuration {
    LessThanFiveSeconds = 'Less than 5s',
    MoreThanFiveSeconds = 'More than 5s'
}

export interface ApiMonitoringViewModel {
    logItems: RequestLog[];
    nextContinuationToken?: string;
}

export const API_MONITORING_PATH = 'ApiMonitoring';

export const getDefaultTimeLimitParams = () => {
    const timeLimitParams = new URLSearchParams();
    timeLimitParams.append(ApiMonitoringQueryParam.StartTimeLimit, subDays(new Date(), 7).toISOString());
    timeLimitParams.append(ApiMonitoringQueryParam.EndTimeLimit, addHours(new Date(), 1).toISOString());
    return timeLimitParams;
};

export const getApiMonitoringPath = (search: string) =>`${encodeURI(`${API_MONITORING_PATH}?${search}`)}&${getDefaultTimeLimitParams().toString()}`;

export const optionalRequestLogsListColumnsWithLabels: Partial<Record<keyof RequestLog, string>> = {
    ['id']: 'Id',
    ['routeTemplate']: 'Route Template',
    ['origin']: 'Origin',
    ['queryString']: 'Query string',
    ['error']: 'Error',
    ['durationInMilliseconds']: 'Duration',
    ['remoteIpAddress']: 'Remote IP address',
    ['identity']: 'Identity name',
    ['agentName']: 'Agent name',
};

export const getOption = (label: string, value: string) => ({ label, value })

export const getSelectOptions = (en: Record<string, string>, labelIdx: number = 0, valueIdx: number = 1) => Object.entries(en).map((k) => getOption(k[labelIdx], k[valueIdx]));

export const getOptionValues = (options: Option[]): string[] => options.map((o) => o.value);

export const getPossibleRequestLogsListColumns = () => Object.keys(optionalRequestLogsListColumnsWithLabels)
    .map((k: keyof RequestLog) => ({ column: k, label: optionalRequestLogsListColumnsWithLabels[k]! }));

export const getQueryParamsFromLocation = () => {
    const queryParams = new URLSearchParams(window.location.search);
    if (!queryParams.has(ApiMonitoringQueryParam.StartTimeLimit) || !queryParams.has(ApiMonitoringQueryParam.EndTimeLimit)) {
        getDefaultTimeLimitParams().forEach((value, key) => queryParams.set(key, value));
    }
    return queryParams;
};

export const getQueryParamsAsObject = (entries: IterableIterator<[string, string]>) => {
    return [...entries].reduce((acc, kvpair) => {
        acc[kvpair[0]] = kvpair[1].split(',');
        return acc;
    }, {} as { [key: string]: string[] });
};

export const getFormattedDuration = (durationInMs: number) => durationInMs < 1000 ? `${durationInMs}ms` : `${(durationInMs / 1000).toFixed(0)}s`;

export const visibleApiMonitoringListColumnsKey = 'frends_apiMonitoringList_visibleColumns';